import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { promptAPI, chatGPTAPI, subscriptionAPI } from '@/lib/api';
import { Sparkles, Copy, Save, ArrowLeft, Loader2, Image, Download, MessageSquare, Send, Crown, Lock } from 'lucide-react';
import { toast } from 'sonner';

const PromptBuilder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') === 'chatgpt' ? 'chatgpt' : 'builder';
  
  const [formData, setFormData] = useState({who: '', where: '', style: '', details: '', mood: '', category: 'image', ai_tool: 'chatgpt'});
  const [generatedPrompt, setGeneratedPrompt] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  
  // ChatGPT Assistant state
  const [hasBusinessPlan, setHasBusinessPlan] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatGeneratedImage, setChatGeneratedImage] = useState(null);
  const [isGeneratingChatImage, setIsGeneratingChatImage] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    checkSubscription();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const checkSubscription = async () => {
    try {
      const status = await subscriptionAPI.getStatus();
      setHasBusinessPlan(status.plan === 'business');
      setIsAdmin(status.is_admin);
    } catch (error) {
      console.log('Could not check subscription');
    }
  };

  const handleGenerate = async () => {
    if (!formData.who || !formData.where || !formData.style || !formData.details || !formData.mood) {
      toast.error('Please fill all fields');
      return;
    }

    setIsGenerating(true);
    try {
      const result = await promptAPI.build(formData);
      setGeneratedPrompt(result);
      toast.success(`Prompt generated! Quality score: ${result.quality_score}/100`);
    } catch (error) {
      console.error('Generate error:', error);
      toast.error('Failed to generate prompt');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    if (generatedPrompt) {
      navigator.clipboard.writeText(generatedPrompt.prompt);
      toast.success('Prompt copied to clipboard!');
    }
  };

  const handleSave = async () => {
    if (!generatedPrompt) return;
    
    setIsSaving(true);
    try {
      await promptAPI.save({title: `${formData.category} prompt`, content: generatedPrompt.prompt, category: formData.category, ai_tool: formData.ai_tool, is_public: true});
      toast.success('Prompt saved to library!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save prompt');
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!generatedPrompt) return;
    
    setIsGeneratingImage(true);
    setGeneratedImage(null);
    try {
      const result = await promptAPI.generateImage(generatedPrompt.prompt);
      if (result.success) {
        setGeneratedImage({
          data: result.image_data,
          mimeType: result.mime_type,
          description: result.description
        });
        toast.success('Image generated successfully!');
      }
    } catch (error) {
      console.error('Image generation error:', error);
      toast.error('Failed to generate image. Please try again.');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleDownloadImage = (imageData, mimeType) => {
    if (!imageData) return;
    try {
      const byteCharacters = atob(imageData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `terrylb-prompt-image-${Date.now()}.png`;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      setTimeout(() => {
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Image downloading...');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download.');
    }
  };

  // ChatGPT Assistant functions
  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setIsChatLoading(true);
    
    try {
      const result = await chatGPTAPI.assist(chatInput, chatMessages);
      const assistantMessage = { role: 'assistant', content: result.response };
      setChatMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      if (error.response?.status === 403) {
        toast.error('ChatGPT Assistant is for Business plan only. Upgrade to unlock!');
      } else {
        toast.error('Failed to get response');
      }
      setChatMessages(prev => prev.slice(0, -1)); // Remove the user message if failed
    } finally {
      setIsChatLoading(false);
    }
  };

  const generateImageFromChat = async (prompt) => {
    setIsGeneratingChatImage(true);
    setChatGeneratedImage(null);
    try {
      const result = await chatGPTAPI.generateImage(prompt);
      if (result.success) {
        setChatGeneratedImage({
          data: result.image_data,
          mimeType: result.mime_type,
          description: result.description
        });
        toast.success('Image generated!');
      }
    } catch (error) {
      console.error('Image error:', error);
      toast.error('Failed to generate image');
    } finally {
      setIsGeneratingChatImage(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const canUseChatGPT = hasBusinessPlan || isAdmin;

  return (
    <div className="min-h-screen" data-testid="prompt-builder">
      <nav className="px-6 py-4 bg-white/80 backdrop-blur-sm border-b-4 border-black sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Sparkles className="h-7 w-7 text-primary" />
          <h1 className="text-xl font-bold">Prompt Builder</h1>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="builder" className="text-lg py-3">
              <Sparkles className="mr-2 h-5 w-5" /> Prompt Builder
            </TabsTrigger>
            <TabsTrigger value="chatgpt" className="text-lg py-3">
              <MessageSquare className="mr-2 h-5 w-5" /> 
              ChatGPT Assistant
              {!canUseChatGPT && <Lock className="ml-2 h-4 w-4" />}
            </TabsTrigger>
          </TabsList>

          {/* Original Prompt Builder Tab */}
          <TabsContent value="builder">
            <h2 className="text-4xl font-bold mb-4 text-center">Build Your <span className="text-primary">Perfect Prompt</span></h2>
            <p className="text-lg text-center mb-8 text-gray-700">Answer a few questions and let AI craft the ideal prompt</p>

            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <Card className="p-8 bg-white border-4 border-black card-shadow">
                  <h3 className="text-2xl font-bold mb-6">Tell Us What You Need</h3>
                  
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-bold mb-2">Who/What is the subject?</label>
                      <Input 
                        value={formData.who}
                        onChange={(e) => setFormData({...formData, who: e.target.value})}
                        placeholder="e.g., a futuristic robot, a young woman"
                        className="border-2 border-black"
                        data-testid="who-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold mb-2">Where is it located?</label>
                      <Input 
                        value={formData.where}
                        onChange={(e) => setFormData({...formData, where: e.target.value})}
                        placeholder="e.g., a neon-lit cityscape, outer space"
                        className="border-2 border-black"
                        data-testid="where-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold mb-2">What style/vibe?</label>
                      <Input 
                        value={formData.style}
                        onChange={(e) => setFormData({...formData, style: e.target.value})}
                        placeholder="e.g., cyberpunk, watercolor, photorealistic"
                        className="border-2 border-black"
                        data-testid="style-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold mb-2">Important details?</label>
                      <Textarea 
                        value={formData.details}
                        onChange={(e) => setFormData({...formData, details: e.target.value})}
                        placeholder="e.g., wearing a leather jacket, glowing eyes"
                        className="border-2 border-black min-h-[80px]"
                        data-testid="details-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold mb-2">Mood/Atmosphere?</label>
                      <Input 
                        value={formData.mood}
                        onChange={(e) => setFormData({...formData, mood: e.target.value})}
                        placeholder="e.g., dramatic, cheerful, mysterious"
                        className="border-2 border-black"
                        data-testid="mood-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold mb-2">Category</label>
                        <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                          <SelectTrigger className="border-2 border-black">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="image">Image</SelectItem>
                            <SelectItem value="video">Video</SelectItem>
                            <SelectItem value="story">Story</SelectItem>
                            <SelectItem value="ad">Product Ad</SelectItem>
                            <SelectItem value="social">Social Post</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="block text-sm font-bold mb-2">AI Tool</label>
                        <Select value={formData.ai_tool} onValueChange={(v) => setFormData({...formData, ai_tool: v})}>
                          <SelectTrigger className="border-2 border-black">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="chatgpt">ChatGPT</SelectItem>
                            <SelectItem value="gemini">Gemini</SelectItem>
                            <SelectItem value="midjourney">Midjourney</SelectItem>
                            <SelectItem value="dall-e">DALL-E</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button 
                      onClick={handleGenerate}
                      disabled={isGenerating}
                      className="bg-primary text-white rounded-lg w-full py-6 text-lg button-shadow font-bold"
                      data-testid="generate-button"
                    >
                      {isGenerating ? (
                        <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Generating...</>
                      ) : (
                        <><Sparkles className="mr-2 h-5 w-5" /> Generate Perfect Prompt</>
                      )}
                    </Button>
                  </div>
                </Card>
              </div>

              <div>
                {generatedPrompt ? (
                  <Card className="p-6 bg-gradient-to-br from-primary/10 to-secondary/10 border-4 border-black card-shadow" data-testid="result-card">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold">Your Perfect Prompt</h3>
                      <div className="bg-green-400 px-3 py-1 rounded-lg border-2 border-black font-bold text-sm">
                        {generatedPrompt.quality_score}/100
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border-2 border-black mb-4 font-mono text-sm max-h-40 overflow-y-auto">
                      {generatedPrompt.prompt}
                    </div>

                    <div className="flex gap-3 mb-4">
                      <Button onClick={handleCopy} className="flex-1 bg-accent text-black rounded-lg font-bold" data-testid="copy-button">
                        <Copy className="mr-2 h-4 w-4" /> Copy
                      </Button>
                      <Button onClick={handleSave} disabled={isSaving} className="flex-1 bg-secondary text-white rounded-lg font-bold" data-testid="save-button">
                        {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Save
                      </Button>
                    </div>

                    <Button 
                      onClick={handleGenerateImage}
                      disabled={isGeneratingImage}
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg py-3 font-bold"
                      data-testid="generate-image-button"
                    >
                      {isGeneratingImage ? (
                        <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Generating...</>
                      ) : (
                        <><Image className="mr-2 h-5 w-5" /> Generate Image with AI</>
                      )}
                    </Button>

                    {generatedImage && (
                      <div className="mt-4 p-3 bg-white rounded-lg border-2 border-black">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-bold">Generated Image</p>
                          <Button onClick={() => handleDownloadImage(generatedImage.data, generatedImage.mimeType)} size="sm" className="bg-green-500 text-white">
                            <Download className="mr-1 h-4 w-4" /> Download
                          </Button>
                        </div>
                        <img src={`data:${generatedImage.mimeType};base64,${generatedImage.data}`} alt="AI Generated" className="w-full rounded-lg" />
                      </div>
                    )}
                  </Card>
                ) : (
                  <Card className="p-8 bg-white/50 border-4 border-dashed border-gray-300 h-full flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <Sparkles className="h-16 w-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg">Fill out the form to generate your prompt!</p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ChatGPT Assistant Tab - Business Only */}
          <TabsContent value="chatgpt">
            {canUseChatGPT ? (
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Chat Interface */}
                <Card className="p-6 bg-white border-4 border-black card-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg">
                      <MessageSquare className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">ChatGPT Prompt Assistant</h3>
                      <p className="text-sm text-gray-600">AI-powered help to craft perfect prompts</p>
                    </div>
                  </div>

                  {/* Chat Messages */}
                  <div className="h-80 overflow-y-auto mb-4 p-3 bg-gray-50 rounded-lg border-2 border-gray-200">
                    {chatMessages.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">
                        <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>Start a conversation! Ask me to help you create prompts.</p>
                        <p className="text-sm mt-2">Try: "Help me create a prompt for a fantasy castle"</p>
                      </div>
                    ) : (
                      chatMessages.map((msg, idx) => (
                        <div key={idx} className={`mb-3 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                          <div className={`inline-block max-w-[85%] p-3 rounded-lg ${
                            msg.role === 'user' 
                              ? 'bg-primary text-white' 
                              : 'bg-white border-2 border-gray-200'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            {msg.role === 'assistant' && (
                              <div className="flex gap-2 mt-2 pt-2 border-t border-gray-200">
                                <Button size="sm" variant="ghost" onClick={() => copyToClipboard(msg.content)} className="text-xs h-7">
                                  <Copy className="h-3 w-3 mr-1" /> Copy
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => generateImageFromChat(msg.content)} className="text-xs h-7">
                                  <Image className="h-3 w-3 mr-1" /> Generate
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                    {isChatLoading && (
                      <div className="text-left">
                        <div className="inline-block p-3 bg-white border-2 border-gray-200 rounded-lg">
                          <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  {/* Chat Input */}
                  <div className="flex gap-2">
                    <Input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                      placeholder="Ask for help with your prompt..."
                      className="border-2 border-black"
                      disabled={isChatLoading}
                    />
                    <Button onClick={sendChatMessage} disabled={isChatLoading || !chatInput.trim()} className="bg-primary text-white px-6">
                      <Send className="h-5 w-5" />
                    </Button>
                  </div>
                </Card>

                {/* Generated Image from Chat */}
                <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-4 border-black card-shadow">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Image className="h-6 w-6 text-green-600" />
                    Generated Image
                  </h3>

                  {isGeneratingChatImage ? (
                    <div className="h-64 flex items-center justify-center">
                      <div className="text-center">
                        <Loader2 className="h-12 w-12 animate-spin text-green-600 mx-auto mb-3" />
                        <p className="text-gray-600">Generating your image...</p>
                      </div>
                    </div>
                  ) : chatGeneratedImage ? (
                    <div>
                      <img 
                        src={`data:${chatGeneratedImage.mimeType};base64,${chatGeneratedImage.data}`} 
                        alt="Generated" 
                        className="w-full rounded-lg border-2 border-gray-200 mb-4"
                      />
                      <Button 
                        onClick={() => handleDownloadImage(chatGeneratedImage.data, chatGeneratedImage.mimeType)} 
                        className="w-full bg-green-600 text-white font-bold"
                      >
                        <Download className="mr-2 h-5 w-5" /> Download Image
                      </Button>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-gray-500">
                      <div className="text-center">
                        <Image className="h-16 w-16 mx-auto mb-3 opacity-50" />
                        <p>Click "Generate" on a chat response to create an image</p>
                      </div>
                    </div>
                  )}
                </Card>
              </div>
            ) : (
              /* Locked State for Non-Business Users */
              <Card className="max-w-2xl mx-auto p-8 bg-gradient-to-br from-amber-50 to-orange-50 border-4 border-amber-400 text-center">
                <Crown className="h-16 w-16 text-amber-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-3">Business Plan Feature</h3>
                <p className="text-gray-700 mb-6">
                  ChatGPT Assistant is an exclusive feature for Business plan subscribers. 
                  Get AI-powered help to craft perfect prompts and generate images directly from the chat!
                </p>
                <div className="bg-white p-4 rounded-lg border-2 border-amber-300 mb-6">
                  <p className="font-bold text-lg mb-2">Business Plan - $89.99/month</p>
                  <ul className="text-sm text-gray-600 text-left space-y-1">
                    <li>✓ ChatGPT Prompt Assistant</li>
                    <li>✓ 500 images/month</li>
                    <li>✓ 100 videos/month</li>
                    <li>✓ All AI Studio tools</li>
                    <li>✓ Lowest overage rates</li>
                    <li>✓ Commercial license</li>
                  </ul>
                </div>
                <Button 
                  onClick={() => navigate('/subscription')}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold px-8 py-4 text-lg"
                >
                  <Crown className="mr-2 h-5 w-5" /> Upgrade to Business
                </Button>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PromptBuilder;
